import { useCurrentAccount } from '../../auth/hooks';
import { isGuestWorkspaceRole } from '../domain/workspaceVisibility';
import { useRoles } from './useRoles';

/**
 * Whether the caller is a Guest of the given asset's workspace -- the
 * allowlist gate every asset composition root and sub-route header applies.
 *
 * Fails closed: `true` (treat as guest) whenever account, roles, or the
 * asset's own workspace id have not resolved -- still loading, OR absent
 * after the query settled (a failed account or roles read leaves `account`
 * null / `roles` empty with `isLoading` false). `isGuestWorkspaceRole` answers
 * `false` for "not provably Guest", which includes missing inputs, so
 * handing it unresolved data would flash the wider, member-only UI. Reads
 * `account.workspaceRoles` (the per-workspace membership record) rather than
 * the caller's ambient/active role, because a Guest opens the asset from a
 * state that carries some OTHER role -- My History (`General`) or their own
 * personal workspace (`Workspace Admin`) -- so the ambient role can't tell a
 * Guest of THIS workspace apart from an ordinary member.
 */
export const useIsGuestOfAssetWorkspace = (
  assetWorkspaceId: string | undefined
): boolean => {
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const { roles, isLoading: isRolesLoading } = useRoles();

  const isUnresolved =
    isAccountLoading ||
    isRolesLoading ||
    !assetWorkspaceId ||
    !account ||
    roles.length < 1;

  return isUnresolved
    ? true
    : isGuestWorkspaceRole(assetWorkspaceId, account.workspaceRoles, roles);
};
